import _ from 'lodash';

import { buildApiActions } from '@/utils/vuex-api-utils';
import { OBFUSCATING_PLAID_BANK_PROVIDERS } from '@/data/obfuscating-plaid-bank-providers';
import {
  BANK_CONNECTION_STATUS_SUMMARY,
  BANK_CONNECTION_ACTIONS,
  BANK_ERROR_STATUSES,
} from '@/data/bank-connection';
import { businessDiligenceApi } from '../../utils/diligence-service';

export default {
  state: {
    bankAccounts: {},
    bankAccountPlaidChecks: {},
    whitelistBankDetails: [],
    plaidMetadata: {},
  },
  getters: {
    bankAccounts: (state) =>
      _.values(state.bankAccounts).filter((acc) => acc.type !== 'vendor'),
    shownBankAccounts(state, getters) {
      const notHiddenAccounts = getters.bankAccounts.filter(
        (ba) => !ba.hiddenAt,
      );
      // put primary account first
      return notHiddenAccounts.sort(
        (ba1, ba2) => ba2.isPrimary - ba1.isPrimary,
      );
    },
    visibleBankAccounts(state, getters) {
      return getters.bankAccounts.filter((ba) => !ba.hiddenAt);
    },
    visibleBankAccountsWithPassedPreFlightChecks(state, getters) {
      const allBankAccountChecks = getters.allBankAccountChecks;

      return getters.visibleBankAccounts.filter((ba) => {
        const bankAccountChecks = allBankAccountChecks[ba.id];

        return bankAccountChecks?.allChecksPass;
      });
    },
    isPrimaryBankAccountDisconnected: (
      state,
      getters,
      rootState,
      rootGetters,
    ) => {
      const intendedPrimaryAccount = getters.bankAccounts.find(
        (ba) => ba.details?.setAsPrimary,
      );
      const accountToCheck =
        intendedPrimaryAccount ?? getters.primaryBankAccount;

      return (
        !rootGetters.businessOnPlaidBypass &&
        (accountToCheck?.connection?.plaidStatus?.action ===
          BANK_CONNECTION_ACTIONS.RELINK ||
          BANK_ERROR_STATUSES.includes(
            accountToCheck?.connection?.plaidStatus?.summary,
          ))
      );
    },
    primaryBankAccount: (state, getters) =>
      _.find(getters.bankAccounts, { isPrimary: true }),
    intendedPrimaryAccount: (_state, getters) =>
      getters.bankAccounts.find((ba) => ba.details?.setAsPrimary),
    primaryOrIntendedPrimaryBankAccount: (_state, getters) =>
      getters.primaryBankAccount ?? getters.intendedPrimaryAccount,
    intendedPrimaryOrPrimaryBankAccount: (_state, getters) =>
      getters.intendedPrimaryAccount ?? getters.primaryBankAccount,
    primaryIsPlaidAccount: (state, getters) =>
      !!getters.intendedPrimaryOrPrimaryBankAccount?.bankConnectionId,
    accountActiveGoCardlessMandates: (state) => (bankAccount) => {
      const activeMandateAccounts = [];
      const { goCardlessAccountDetails } = bankAccount;
      if (goCardlessAccountDetails?.length) {
        goCardlessAccountDetails.forEach((gcAccountDetails) => {
          const isMandateActive = [
            'created',
            'pending_submission',
            'submitted',
            'active',
          ].includes(gcAccountDetails.gcStatus);

          if (isMandateActive) {
            activeMandateAccounts.push(gcAccountDetails);
          }
        });
      }
      return activeMandateAccounts;
    },
    accountHasActiveGoCardlessMandate: (state, getters) => (bankAccount) => {
      const activeMandates =
        getters.accountActiveGoCardlessMandates(bankAccount);

      return activeMandates.length;
    },
    primaryBankAccountAuthorizedGoCardless: (state, getters) => {
      const { primaryBankAccount, accountHasActiveGoCardlessMandate } = getters;
      return primaryBankAccount
        ? accountHasActiveGoCardlessMandate(primaryBankAccount)
        : false;
    },
    hasUploadedBankDocs: (state, getters, rootState, rootGetters) => {
      return (
        rootGetters.uploadsMatching(rootGetters.bankUploadMatcher).length > 0
      );
    },
    fiscalDocumentsUpToDate: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.maxCapacityOverThreshold) {
        return true;
      }

      return rootGetters.fiscalDocumentChecks?.lastQuartersStatementUploaded;
    },
    isInFinancialDocsSegment: (state, getters, rootState, rootGetters) => {
      return rootGetters.maxCapacityOverThreshold;
    },
    currentBankAccountToBePrimary: (_state, getters) => {
      if (getters.bankAccounts.length) {
        return getters.bankAccounts.find(
          (bankAccount) => bankAccount.details?.setAsPrimary,
        );
      }
      return null;
    },
    isBankAccountToBePrimaryObfuscatingData: (_state, getters) => {
      if (
        getters.bankAccounts.length &&
        getters.currentBankAccountToBePrimary
      ) {
        return OBFUSCATING_PLAID_BANK_PROVIDERS.includes(
          getters.currentBankAccountToBePrimary.bankName,
        );
      }
      return false;
    },
    allBankAccountChecks: (state) => {
      return state.bankAccountPlaidChecks;
    },
    whitelistBankDetails: (state) => {
      return state.whitelistBankDetails;
    },
    whitelistRoutingNumbers: (state, getters) => {
      return getters.whitelistBankDetails.map(
        (details) => details.routingNumber,
      );
    },
    whitelistDetailsForRoutingNumber: (state, getters) => (routingNumber) => {
      return getters.whitelistBankDetails.find(
        (details) => details.routingNumber === routingNumber,
      );
    },
    plaidMetadata: (state, getters) => {
      return state.plaidMetadata;
    },
    primaryBankAccountNumberIsTokenized: (state, getters) => {
      return getters.intendedPrimaryOrPrimaryBankAccount?.bankName === 'Chase';
    },
    isAccountInConnectingState: () => (account) => {
      return [
        BANK_CONNECTION_STATUS_SUMMARY.CONNECTING,
        BANK_CONNECTION_STATUS_SUMMARY.INITIAL_SCRAPE,
        BANK_CONNECTION_STATUS_SUMMARY.SCRAPING,
      ].includes(account?.connection?.plaidStatus?.summary);
    },
    anyBankAccountInConnectingState: (state, getters) => {
      return getters.bankAccounts.some((account) =>
        getters.isAccountInConnectingState(account),
      );
    },
  },
  ...buildApiActions(
    {
      FETCH_USER_BANK_ACCOUNTS: {
        action: (ctx, payload) => ({
          method: 'get',
          url: `/users/${ctx.rootGetters.userId}/bank-accounts`,
          params: payload,
        }),
        mutation: (state, { response }) => {
          state.bankAccounts = _.keyBy(response.bankAccounts, 'id');
        },
      },
      ADD_USER_BANK_CONNECTION: {
        action: (ctx, payload) => ({
          method: 'post',
          // TODO: switch to business
          url: `/users/${ctx.rootGetters.userId}/bank-connections`,
          params: payload,
          options: { timeout: 60000 }, // temporary measure to ensure no timeout on bank connection
          async afterSuccess() {
            // TODO: figure out how to deal with this
            await ctx.dispatch('api-FETCH_USER_BANK_ACCOUNTS');
          },
        }),
        // mutation: (state, { response }) => { state.bankAccounts = _.keyBy(response, 'id'); },
      },
      RECONNECT_USER_BANK_CONNECTION: {
        action: (ctx, payload) => ({
          method: 'post',
          url: `/bank-connections/${payload.bankConnectionId}/reconnect`,
          async afterSuccess() {
            // TODO: figure out how to deal with this
            await ctx.dispatch('api-FETCH_USER_BANK_ACCOUNTS');
          },
        }),
        // mutation: (state, { response }) => { state.bankAccounts = _.keyBy(response, 'id'); },
      },
      ADD_USER_BANK_ACCOUNT: {
        action: (ctx, payload) => ({
          method: 'post',
          // TODO: switch to business
          url: `/users/${ctx.rootGetters.userId}/bank-accounts`,
          params: payload,
          returnResponse: true, // returns the api response directly from the action!
        }),
        mutation: (state, { response }) => {
          state.bankAccounts[response.id] = response;
        },
      },
      GET_PLAID_LINK_TOKEN: {
        action: (ctx, payload) => ({
          method: 'post',
          url: '/link-token',
          params: payload,
          returnResponse: true,
        }),
      },
      UPDATE_BANK_ACCOUNT: {
        action: (ctx, payload) => ({
          method: 'patch',
          url: `/bank-accounts/${payload.id}`,
          params: _.omit(payload, 'id'),
        }),
        mutation: (state, { response }) => {
          state.bankAccounts[response.id] = response;
        },
      },
      SET_BANK_ACCOUNT_AS_PRIMARY: {
        action: (ctx, payload) => ({
          method: 'post',
          url: `/bank-accounts/${payload.id}/set-primary`,
          params: payload,
          async afterSuccess() {
            await ctx.dispatch('api-FETCH_USER_BANK_ACCOUNTS');
          },
        }),
        mutation: (state, { response }) => {
          state.bankAccounts[response.id] = response;
        },
      },
      SIGN_BANK_ACCOUNT_AUTH_FORM: {
        action: (ctx, payload) => ({
          method: 'post',
          url: `/contracts/${payload.bankAccountId}/embedded-signing`,
          params: { returnURL: payload.returnURL?.split('?')[0] ?? null },
          options: { timeout: 15000 },
          returnResponse: true,
        }),
        mutation: (state, { actionSpec, response }) => {
          state.bankAccounts[
            actionSpec.payload.bankAccountId
          ].embeddedSigningSession = response.url;
        },
      },
      GET_BANK_ACCOUNT_CHECKS: {
        action: (ctx, payload) => ({
          requestFunc: async () => {
            const response =
              await businessDiligenceApi.businessDiligenceControllerPreFlightChecks(
                payload.businessId,
                payload.bankAccountId,
              );
            return response;
          },
        }),
        mutation: (state, { payload, response }) => {
          state.bankAccountPlaidChecks[payload.bankAccountId] = response;
        },
      },
      GET_BANKS_ON_PLAID_WHITELIST: {
        action: (ctx, payload) => ({
          method: 'get',
          url: '/plaid-whitelist-banks',
          returnResponse: true,
        }),
        mutation: (state, { payload, response }) => {
          state.whitelistBankDetails = response;
        },
      },
    },
    {
      mutations: {
        UPDATE_PLAID_METADATA_: (state, metadata) => {
          state.plaidMetadata = metadata;
        },
      },
    },
  ),
};
